import React, { use } from "react";
import { CaretController } from "../../../utils/ScrollController";
import {
    MetronomeMarking,
    TimeSignature
} from "../../../model/core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faBackward,
    faCircle,
    faFastBackward,
    faFastForward,
    faForward,
    faPause,
    faPlay,
    faStop,
    faUndo
} from "@fortawesome/free-solid-svg-icons";
import { useMidiAccessStore } from "../../../stores/MidiAccessStore";
import {
    findLargestPPQ,
    RydProject
} from "../../../model/project";
import { RydToggleButton } from "../../widgets/RydToggleButton";
import { RecordArmController } from "../../../audio/RecordingController";
import { setupPlayerSession } from "../../../audio/PlaybackController";
import {
    useIsPaused,
    useIsPlaying,
    useIsRecording,
    useLoopPlaybackSetup,
    usePlayerSession,
    useSetIsPaused,
    useSetIsPlaying,
    useSetIsRecording,
    useSetLoopPlaybackSetup,
    useSetPlayerSession
} from "../../../stores/PlaybackConfStore";
import RydRollHeaderGroup from "../../widgets/RydRollHeaderGroup";


export function GlobalPlaybackControls({
    project,
    timeSignature,
    caretController,
}: {
    project: RydProject,
    timeSignature: TimeSignature,
    caretController?: CaretController,
}) {

    const isPlaying = useIsPlaying();
    const setIsPlaying = useSetIsPlaying();

    const isPaused = useIsPaused();
    const setIsPaused = useSetIsPaused();

    const isRecording = useIsRecording();
    const setIsRecording = useSetIsRecording();

    const loopPlaybackSetup = useLoopPlaybackSetup();
    const setLoopPlaybackSetup = useSetLoopPlaybackSetup();

    const playerSession = usePlayerSession();
    const setPlayerSession = useSetPlayerSession();

    const stopAll = () => {
        playerSession?.stop();
        setIsPlaying(false);
        setIsPaused(false);
        setIsRecording(false);
        RecordArmController.getInstance().unsetCurrentRecordingTick();
    };

    return (<RydRollHeaderGroup>
        <button onClick={() => {
            if (caretController) {
                caretController.scrollToTick(0);
            }
        }}>
            <FontAwesomeIcon icon={faFastBackward} />
        </button>

        <button onClick={() => {
            if (caretController) {
                caretController.scrollToTick(Math.max(0, caretController.getCaretPosition() - (findLargestPPQ(project))));
            }
        }}>
            <FontAwesomeIcon icon={faBackward} />
        </button>

        {!isPlaying && <button
            onClick={() => {
                setIsPlaying(true);
                const player = setupPlayerSession({
                    project,
                    timeSignature,
                    caretController,
                    loopPlaybackSetup : loopPlaybackSetup,
                    recording: false
                });
                setPlayerSession(player);
                player?.start();
            }}
            disabled={isPlaying}
        >
            <FontAwesomeIcon icon={faPlay} />
        </button>
        }
        {
            // Pause
            !isPaused && isPlaying && <button
                disabled={!isPlaying || isRecording}
                onClick={() => {
                    setIsPaused(true);
                    // pauseSequence();
                    playerSession?.pause();
                }}
            >
                <FontAwesomeIcon icon={faPause} />
            </button>
        }
        {
            // Continue
            isPaused && <button
                disabled={!isPlaying}
                onClick={() => {
                    setIsPaused(false);
                    playerSession?.start();
                }}
            >
                <FontAwesomeIcon icon={faPlay} />
            </button>
        }
        {/* Stop */}
        <button
            onClick={stopAll}
            disabled={!isPlaying}
        >
            <FontAwesomeIcon icon={faStop} />
        </button>

        {/* Record */}
        <button
            onClick={() => {
                if (isRecording) return;

                // Start capturing
                setIsRecording(true);
                setIsPlaying(true);
                const player = setupPlayerSession({
                    project,
                    timeSignature,
                    caretController,
                    loopPlaybackSetup: loopPlaybackSetup,
                    recording: true
                });
                setPlayerSession(player);
                player?.start();
            }}
            style={isRecording ? { color: 'red' } : {}}
        >
            <FontAwesomeIcon icon={faCircle} />
        </button>

        {/* Forward caret */}
        <button onClick={() => {
            if (caretController) {
                caretController.scrollToTick(Math.min(project.getLength(), caretController.getCaretPosition() + (findLargestPPQ(project))));
            }
        }}>
            <FontAwesomeIcon icon={faForward} />
        </button>

        {/* To end caret */}
        <button onClick={() => {
            if (caretController) {
                caretController.scrollToTick(project.getLength());
            }
        }}>
            <FontAwesomeIcon icon={faFastForward} aria-description="fast-forward"/>
        </button>

        <RydToggleButton
            active={loopPlaybackSetup.isLooping}
            onActivate={() => setLoopPlaybackSetup({ ...loopPlaybackSetup, isLooping: true })}
            onDeactivate={() => setLoopPlaybackSetup({ ...loopPlaybackSetup, isLooping: false })}
            activeStyle={{
                backgroundColor: 'lightgreen',
                width: '30px !important',
                color: 'var(--md-sys-color-on-secondary-fixed)'
            }}
            inactiveStyle={{
                backgroundColor: 'lightgray',
                width: '30px !important',
                color: 'var(--md-sys-color-on-secondary-fixed)'
            }}
        >
            <FontAwesomeIcon icon={faUndo} aria-description="loop" />
        </RydToggleButton>
    </RydRollHeaderGroup>);
}
